import { IkeToken } from '@ikegps/core-api';
import { type IkeDraw, IkeDrawBindings, type SVGRenderOptions } from '@ikegps/core-shared';
import type { CollectionModelDrawable } from '@ikegps/core-shared';
import { action, makeObservable } from 'mobx';
import { Services } from '../../services';
import { Code } from '../../services/input/code.enum';
import type { InputEventBindContext } from '../../services/input/event.context';
import type { EventData } from '../../services/input/input.service';
import { CurrentState } from '../../state/page.state';
import type { PhotoMeasurePage } from '../photomeasure.page';

export class PhotoMeasureInputEvents {
	eventContext: InputEventBindContext;
	page: PhotoMeasurePage;
	draw: IkeDraw;
	eventCount: { [eventName: string]: number } = {};

	renderOptions: SVGRenderOptions | null = null;

	constructor(page: PhotoMeasurePage, draw: IkeDraw) {
		makeObservable(this);

		this.page = page;
		this.draw = draw;
		this.eventContext = Services.Input.getContext();
	}

	private incrementCount(title: string) {
		this.eventCount[title] = (this.eventCount[title] || 0) + 1;
	}

	init() {
		this.eventContext = Services.Input.getContext();
		this.eventContext.on(Code.KeyS, this.saveCollection, { modifiers: { ctrl: true } });

		this.eventContext.on(Code.ArrowUp, this.moveSelectedX.bind(this, -1));
		this.eventContext.on(Code.ArrowUp, this.moveSelectedX.bind(this, -25), { modifiers: { shift: true } });
		this.eventContext.on(Code.ArrowUp, this.moveSelectedX.bind(this, -0.25), { modifiers: { ctrl: true } });

		this.eventContext.on(Code.ArrowDown, this.moveSelectedX.bind(this, 1));
		this.eventContext.on(Code.ArrowDown, this.moveSelectedX.bind(this, 25), { modifiers: { shift: true } });
		this.eventContext.on(Code.ArrowDown, this.moveSelectedX.bind(this, 0.25), { modifiers: { ctrl: true } });

		this.eventContext.on(Code.ArrowLeft, this.moveSelectedY.bind(this, -1));
		this.eventContext.on(Code.ArrowLeft, this.moveSelectedY.bind(this, -25), { modifiers: { shift: true } });
		this.eventContext.on(Code.ArrowLeft, this.moveSelectedY.bind(this, -0.25), { modifiers: { ctrl: true } });

		this.eventContext.on(Code.ArrowRight, this.moveSelectedY.bind(this, 1));
		this.eventContext.on(Code.ArrowRight, this.moveSelectedY.bind(this, 25), { modifiers: { shift: true } });
		this.eventContext.on(Code.ArrowRight, this.moveSelectedY.bind(this, 0.25), { modifiers: { ctrl: true } });

		const isIkeAnalyst = Services.User.current && Services.User.isIkeAnalyst(Services.User.current);

		if (Services.Token.has(IkeToken.FEATURE_INSIGHT_AHOA) || isIkeAnalyst) {
			this.eventContext.on(
				Code.KeyI,
				() => this.draw.state.setInsightMarkersVisible(!this.draw.state.visibleInsightMarkers),
				{ modifiers: { ctrl: true } }
			);
			this.eventContext.on(
				Code.KeyM,
				() => this.draw.state.setInsightMasksVisible(!this.draw.state.visibleInsightMasks),
				{ modifiers: { ctrl: true } }
			);
		}

		this.eventContext.on(Code.Escape, this.clearSelected);

		// Arrange Annotations
		if (Services.Token.has(IkeToken.FEATURE_ANNOTATIONS_ARRANGE) || isIkeAnalyst) {
			this.eventContext.on(
				Code.KeyA,
				(e) => {
					if (!this.renderOptions || !this.draw) {
						return;
					}
					e.event.preventDefault();
					this.draw.state.setAutoArrangeAnnotations(!this.draw.state.autoArrangeAnnotations, this.renderOptions);
				},
				{ modifiers: { ctrl: true, shift: true } }
			);
		}
	}

	getSelectedObject(): CollectionModelDrawable | null {
		if (this.draw == null) {
			return null;
		}
		return this.draw.selected;
	}

	trackEvents() {
		for (const eventLabel of Object.keys(this.eventCount)) {
			const value = this.eventCount[eventLabel];
			if (value == null || value === 0) {
				continue;
			}
			Services.Analytics.trackEvent(
				Services.Analytics.EventCategory.Collection,
				eventLabel,
				CurrentState.inspectedCaptureId ?? undefined,
				value
			);
		}
	}

	@action.bound clearSelected(evt: EventData) {
		CurrentState.cancelInteractions();

		this.incrementCount('keyboard:clear');
		const selected = this.getSelectedObject();
		if (selected == null) {
			return;
		}
		this.draw.state.setSelected(null, null);
	}

	@action.bound saveCollection(evt: EventData) {
		if (this.draw == null) {
			return;
		}
		evt.event.preventDefault();
		this.incrementCount('keyboard:save');
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.page.editControl.saveChanges(evt.event);
	}

	@action.bound deleteSelected(evt: EventData) {
		const selected = this.getSelectedObject();
		if (selected == null || !this.draw.state.selectedId) {
			return;
		}

		this.incrementCount('keyboard:delete');
		IkeDrawBindings.Measurement.delete(this.draw, { drawableId: this.draw.state.selectedId, $event: evt.event });
	}

	@action moveSelectedX(dy: number, evt: EventData) {
		const selected = this.getSelectedObject();
		if (selected == null) {
			return;
		}
		if (!this.renderOptions || !this.draw) {
			return;
		}
		const isUp = dy < 0;
		this.incrementCount(`keyboard:move:${isUp ? 'up' : 'down'}:${Math.abs(dy)}`);
		IkeDrawBindings.Selection.move(this.draw, { delta: { x: 0, y: dy }, $event: evt.event });
		if (this.draw.state.autoArrangeAnnotations) {
			this.draw.state.arrangeAnnotations(this.renderOptions);
		}
	}

	@action moveSelectedY(dx: number, evt: EventData) {
		const selected = this.getSelectedObject();
		if (selected == null) {
			return;
		}

		const isLeft = dx < 0;
		this.incrementCount(`keyboard:move:${isLeft ? 'left' : 'right'}:${Math.abs(dx)}`);
		// Don't allow keyboard events to move labels
		const selectedRegion = this.draw.state.selectedRegion;
		const label = selectedRegion === 'label' ? null : selectedRegion;
		IkeDrawBindings.Selection.move(this.draw, {
			delta: { x: dx, y: 0 },
			$event: evt.event,
			label: label ?? undefined,
		});
	}

	destroy() {
		if (this.eventContext != null) {
			this.eventContext.destroy();
		}
	}
}
